"use client";

import { useMemo } from "react";

import { ListingStatus } from "@/.zenstack/logical-prisma-client";
import BaseListingCarousel from "@/components/listing-carousels/BaseListingCarousel";
import { LISTING_CARD_SELECT } from "@/components/listings/ListingCard";
import { useSession } from "@/lib/auth/client";
import {
  type SavedListingInfo,
  useSavedListings,
} from "@/lib/dao/SavedListingDao";
import { useFindManyListing } from "@/lib/hooks/generated/tanstack-query";

export default function SavedListingCarousel() {
  const { data: session, isPending } = useSession();
  const { savedListings = [] } = useSavedListings();

  // Get the listing IDs from saved listings, ordered by most recent save first
  const savedListingIds = useMemo(() => {
    if (!session?.user?.id) return [];

    return savedListings
      .filter((sl: SavedListingInfo) => sl.isSaved)
      .sort(
        (a: SavedListingInfo, b: SavedListingInfo) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      )
      .map((sl: SavedListingInfo) => sl.listingId);
  }, [savedListings, session?.user?.id]);

  // Fetch the actual listing data for saved listings
  const { data: listings, isLoading } = useFindManyListing({
    select: LISTING_CARD_SELECT,
    where: {
      id: {
        in: savedListingIds,
      },
      status: ListingStatus.ACTIVE,
    },
    orderBy: { updatedAt: "desc" },
  });

  // Only show for authenticated users
  if (!session?.user || isPending) {
    return null;
  }

  return (
    <BaseListingCarousel
      heading="Your Saved Listings"
      listings={listings}
      isLoading={isLoading}
      emptyMessage="You haven't saved any listings yet."
      delayTime={10000} // Auto-advance every 10 seconds
    />
  );
}
